import { createTheme } from '@mui/material/styles';

export const createAppTheme = (mode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      primary: {
        main: '#0f172a',
        light: '#1e293b',
        dark: '#020617',
        contrastText: '#ffffff'
      },
      secondary: {
        main: '#0d9488',
        light: '#14b8a6',
        dark: '#0f766e',
        contrastText: '#ffffff'
      },
      background: {
        default: isDark ? '#0b1120' : '#f8fafc',
        paper: isDark ? '#1a2540' : '#ffffff'
      },
      text: {
        primary: isDark ? '#e2e8f0' : '#0f172a',
        secondary: isDark ? '#94a3b8' : '#475569'
      },
      action: {
        active: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.54)',
        hover: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
        selected: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.08)',
        disabled: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
        disabledBackground: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'
      },
      divider: isDark ? '#334155' : '#e2e8f0'
    },
    typography: {
      fontFamily: '"Source Sans 3", "Helvetica Neue", Arial, sans-serif',
      h1: { fontFamily: '"Onest", sans-serif', fontWeight: 700 },
      h2: { fontFamily: '"Onest", sans-serif', fontWeight: 700 },
      h3: { fontFamily: '"Onest", sans-serif', fontWeight: 700 },
      h4: { fontFamily: '"Onest", sans-serif', fontWeight: 700 },
      h5: { fontFamily: '"Onest", sans-serif', fontWeight: 600 },
      h6: { fontFamily: '"Onest", sans-serif', fontWeight: 600 },
      button: { fontWeight: 500, textTransform: 'none' }
    },
    shape: { borderRadius: 10 },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundColor: '#0f172a', boxShadow: 'none', borderBottom: '1px solid #1e293b' }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: '#0f172a', borderRight: 'none' }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 6px',
            width: 'calc(100% - 12px)',
            '&.Mui-selected': { backgroundColor: '#0d9488', '&:hover': { backgroundColor: '#0f766e' } },
            '&:hover': { backgroundColor: '#1e293b' }
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: { root: { minWidth: 34, color: '#94a3b8' } }
      },
      MuiListItemText: {
        styleOverrides: { primary: { color: '#e2e8f0', fontSize: '0.875rem', fontWeight: 500 } }
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 500 },
          containedPrimary: {
            backgroundColor: '#0d9488',
            '&:hover': { backgroundColor: '#0f766e' }
          },
          text: {
            color: isDark ? '#94a3b8' : '#475569',
            '&:hover': {
              color: isDark ? '#e2e8f0' : '#0f172a',
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: isDark ? '#94a3b8' : '#64748b',
            '&:hover': { color: isDark ? '#e2e8f0' : '#0f172a' }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            boxShadow: isDark
              ? '0 1px 3px 0 rgba(0,0,0,0.3)'
              : '0 1px 3px 0 rgba(0,0,0,0.08), 0 1px 2px -1px rgba(0,0,0,0.06)'
          }
        }
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
              fontWeight: 600,
              color: isDark ? '#e2e8f0' : '#0f172a'
            }
          }
        }
      },
      MuiDataGrid: {
        styleOverrides: {
          root: isDark
            ? {
                border: '1px solid #334155',
                borderRadius: 12,
                backgroundColor: '#1a2540',
                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1e293b', borderBottom: '2px solid #334155' },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  color: '#e2e8f0',
                  fontSize: '0.78rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#1e293b' },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: '#0f2a2a',
                  '&:hover': { backgroundColor: '#1a3838' }
                },
                '& .MuiDataGrid-cell': { borderColor: '#1e293b', color: '#cbd5e1', fontSize: '0.875rem' },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #334155',
                  backgroundColor: '#1e293b',
                  color: '#94a3b8'
                },
                '& .MuiDataGrid-toolbarContainer': {
                  padding: '8px 12px',
                  borderBottom: '1px solid #1e293b',
                  gap: '6px'
                },
                '& .MuiButton-text': { color: '#94a3b8' },
                '& .MuiButton-text:hover': { color: '#e2e8f0', backgroundColor: 'rgba(255,255,255,0.06)' }
              }
            : {
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                backgroundColor: '#ffffff',
                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f1f5f9', borderBottom: '2px solid #e2e8f0' },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: '0.78rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#f8fafc' },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: '#f0fdfa',
                  '&:hover': { backgroundColor: '#ccfbf1' }
                },
                '& .MuiDataGrid-cell': { borderColor: '#f1f5f9', color: '#334155', fontSize: '0.875rem' },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  color: '#475569'
                },
                '& .MuiDataGrid-toolbarContainer': {
                  padding: '8px 12px',
                  borderBottom: '1px solid #f1f5f9',
                  gap: '6px'
                }
              }
        }
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } }
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 16 } }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: '"Onest", sans-serif',
            fontWeight: 600,
            color: isDark ? '#e2e8f0' : '#0f172a'
          }
        }
      }
    }
  });
};

export default createAppTheme('dark');
